import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { UserContext } from 'Context/user';

import { CONVERSION_LOCALSTORAGE_KEY, CONVERSION_URL_ROUTES, OPTIMIZELY_ID, OPTIMIZELY_SCRIPT, OPTIMIZELY_WEB_KEY } from 'Utilities/conversion';
import { VRM_REGEX } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';

const CACHE_NAME = 'conversion';

const LoadOptimizelyScript = () => {
  const [isScriptAdded, setIsScriptAdded] = useState(false);
  const { userState } = useContext(UserContext);
  const { pathname } = useLocation();

  const IS_USER = userState?.id;

  const addOptimizelyTag = useCallback((content?: string) => {
    const script = document.createElement('script');
    script.id = OPTIMIZELY_ID;
    script.crossOrigin = 'anonymous';

    if (content) {
      script.textContent = content;
    } else {
      script.src = OPTIMIZELY_SCRIPT;
    }

    safeLocalStorage.setItem(CONVERSION_LOCALSTORAGE_KEY, IS_USER);

    document.head.appendChild(script);
    setIsScriptAdded(true);
  }, [IS_USER]);

  const optimizelyError = (err: Error) => {
    window?.Sentry?.captureException(err, {
      tags: {
        fetch: CACHE_NAME,
      },
    });
  };

  useEffect(() => {
    caches.open(CACHE_NAME).then((cache) => {
      cache.add(OPTIMIZELY_SCRIPT)
        .catch((error) => {
          addOptimizelyTag();
          optimizelyError(error);
        });
    })
      .catch((error) => {
        addOptimizelyTag();
        optimizelyError(error);
      });
  }, [addOptimizelyTag]);

  useEffect(() => {
    if (isScriptAdded || !IS_USER) {
      return;
    }

    const IS_CONVERSION_ROUTE = VRM_REGEX.test(pathname.replace(/^\//, '').split('/')[0])
      || CONVERSION_URL_ROUTES.some((route) => pathname === route);

    if (IS_CONVERSION_ROUTE) {
      caches.open(CACHE_NAME).then(async (cache) => {
        addOptimizelyTag(await cache
          .match(OPTIMIZELY_SCRIPT)
          .then((response) => response?.text())
          .catch((error) => {
            optimizelyError(error);
            return undefined;
          }));
      });
    }
  }, [addOptimizelyTag, pathname, IS_USER, isScriptAdded]);

  return null;
};

export const OptimizelyScript = () => {
  const OPTIMIZELY_NEEDED = OPTIMIZELY_WEB_KEY && !globalThis?.document?.getElementById?.(OPTIMIZELY_ID);

  return OPTIMIZELY_NEEDED
    ? <LoadOptimizelyScript />
    : null;
};
